import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './LoginPage.css';

// Note: The OIDC access_token received from Okta after PKCE login is stored
// directly and used to authorize all API calls. No secondary M2M token exchange is needed.

// PKCE Helpers

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer) {
  let str = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateState() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

function decodeJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    console.warn('Could not decode JWT payload:', e);
    return {};
  }
}

function determineRole(groups) {
  const adminGroup = process.env.REACT_APP_OKTA_ADMIN_GROUP || 'O_CONNECT_ADMIN_APP_ADMINUSER';
  const standardGroup = process.env.REACT_APP_OKTA_STANDARD_GROUP || 'O_CONNECT_ADMIN_APP_STANDARDUSER';
  if (!groups || !Array.isArray(groups)) {
    console.warn('No groups claim found in ID token');
    return 'STANDARD';
  }
  if (groups.includes(adminGroup)) return 'ADMIN';
  if (groups.includes(standardGroup)) return 'STANDARD';
  console.warn('User does not belong to any recognized group:', groups);
  return 'STANDARD';
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [error, setError] = useState(null);
  const from = location.state?.from?.pathname || '/home';
  const oktaIssuer = process.env.REACT_APP_OKTA_ISSUER;
  const oktaClientId = process.env.REACT_APP_OKTA_CLIENT_ID;
  const oktaRedirectUri = process.env.REACT_APP_OKTA_REDIRECT_URI || `${window.location.origin}/home`;

  // Guard: prevent redirectToOkta() from firing more than once per mount.
  // redirectToOkta is async (awaits generateCodeChallenge) — without this guard,
  // a React re-render triggered by setLoadingMessage() inside redirectToOkta can
  // cause the useEffect to re-fire (if navigate/handleOktaCallback get new references
  // from BrowserRouter re-rendering its context), calling redirectToOkta() again
  // BEFORE okta_flow has been written to sessionStorage. This produces a 429 loop.
  const redirectInitiatedRef = useRef(false);

  const handleOktaCallback = useCallback(async (code) => {
    setLoading(true);
    setLoadingMessage('Exchanging authorization code...');
    setError(null);
    try {
      const codeVerifier = sessionStorage.getItem('okta_code_verifier');
      if (!codeVerifier) {
        setError('PKCE code verifier not found. Please try logging in again.');
        setLoading(false);
        return;
      }
      const savedState = sessionStorage.getItem('okta_state');
      const urlParams = new URLSearchParams(window.location.search);
      const returnedState = urlParams.get('state');
      if (savedState && returnedState && savedState !== returnedState) {
        setError('Security validation failed (state mismatch). Please try again.');
        sessionStorage.removeItem('okta_code_verifier');
        sessionStorage.removeItem('okta_state');
        sessionStorage.removeItem('okta_flow');
        setLoading(false);
        return;
      }
      const tokenUrl = oktaIssuer + '/v1/token';
      const tokenParams = new URLSearchParams();
      tokenParams.append('grant_type', 'authorization_code');
      tokenParams.append('client_id', oktaClientId);
      tokenParams.append('code', code);
      tokenParams.append('redirect_uri', oktaRedirectUri);
      tokenParams.append('code_verifier', codeVerifier);
      const tokenResponse = await axios.post(tokenUrl, tokenParams, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      const data = tokenResponse.data;
      sessionStorage.removeItem('okta_code_verifier');
      sessionStorage.removeItem('okta_state');
      sessionStorage.removeItem('okta_flow');
      let userInfo = {};
      if (data.id_token) {
        userInfo = decodeJwtPayload(data.id_token);
      }
      const role = determineRole(userInfo.groups);
      console.log('User groups:', userInfo.groups, 'Role (from groups):', role);
      // TODO: TESTING ONLY — force ADMIN role regardless of groups claim.
      // Remove this override before merging to prod.
      const effectiveRole = 'ADMIN';
      const expiresAt = new Date(Date.now() + (data.expires_in || 3600) * 1000).toISOString();
      const userData = {
        id_token: data.id_token,
        login_access_token: data.access_token,
        refresh_token: data.refresh_token,
        lan_id: userInfo.preferred_username || userInfo.email || userInfo.sub || 'okta-user',
        name: userInfo.name || userInfo.preferred_username || 'User',
        email: userInfo.email || '',
        sub: userInfo.sub || '',
        groups: userInfo.groups || [],
        // role: role,
        role: effectiveRole,
        auth_method: 'okta',
        expires_at: expiresAt,
      };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      // Store the OIDC access token — used as Bearer token for all API calls
      localStorage.setItem('okta_api_token', data.access_token);
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Okta callback error:', err);
      const errMsg = err.response?.data?.error_description || err.response?.data?.error || err.message;
      setError('Failed to complete Okta authentication: ' + errMsg);
      sessionStorage.removeItem('okta_code_verifier');
      sessionStorage.removeItem('okta_state');
      sessionStorage.removeItem('okta_flow');
    } finally {
      setLoading(false);
    }
  }, [oktaIssuer, oktaClientId, oktaRedirectUri, from, navigate]);

  const redirectToOkta = useCallback(async () => {
    if (!oktaIssuer || !oktaClientId) {
      setError('Okta is not configured. Please contact your administrator.');
      setLoading(false);
      return;
    }
    try {
      setLoadingMessage('Redirecting to Okta...');
      const scopes = process.env.REACT_APP_OKTA_SCOPES || 'openid profile email groups offline_access';
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();
      sessionStorage.setItem('okta_code_verifier', codeVerifier);
      sessionStorage.setItem('okta_state', state);
      sessionStorage.setItem('okta_flow', 'login');
      const authUrl = new URL(oktaIssuer + '/v1/authorize');
      authUrl.searchParams.set('client_id', oktaClientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scopes);
      authUrl.searchParams.set('redirect_uri', oktaRedirectUri);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Failed to initiate Okta login:', err);
      setError('Failed to start Okta login. Please try again.');
      setLoading(false);
    }
  }, [oktaIssuer, oktaClientId, oktaRedirectUri]);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.expires_at) {
          const expiresAt = new Date(userData.expires_at).getTime();
          if (Date.now() < expiresAt) {
            navigate(from, { replace: true });
            return;
          }
        } else if (userData.lan_id || userData.api_access_token) {
          navigate(from, { replace: true });
          return;
        }
      } catch {
        localStorage.removeItem('user');
      }
    }
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    if (errorParam) {
      setError('Authentication failed: ' + (errorDescription || errorParam));
      setLoading(false);
      sessionStorage.removeItem('okta_code_verifier');
      sessionStorage.removeItem('okta_state');
      sessionStorage.removeItem('okta_flow');
      // Strip ?error= params from the URL so a re-render doesn't re-trigger this block
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    if (code) {
      const flow = sessionStorage.getItem('okta_flow');
      if (flow === 'login') {
        handleOktaCallback(code);
        return;
      }
    }
    // Guard against duplicate redirects (see redirectInitiatedRef comment above)
    if (redirectInitiatedRef.current) return;
    redirectInitiatedRef.current = true;
    redirectToOkta();
  }, [from, navigate, handleOktaCallback, redirectToOkta]);

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon">&#9729;&#65039;</span>
            <h1>Connect Agent Setup</h1>
          </div>
          <p>Amazon Connect Administration Portal</p>
        </div>
        {error && (
          <div className="login-error">
            <div className="login-error-icon">&#9888;&#65039;</div>
            <p className="login-error-message">{error}</p>
            <button className="login-retry-btn" onClick={() => { setError(null); redirectInitiatedRef.current = false; redirectToOkta(); }}>
              Try Again
            </button>
          </div>
        )}
        {loading && !error && (
          <div className="login-loading">
            <div className="login-spinner"></div>
            <p className="login-loading-message">{loadingMessage}</p>
            <p className="login-loading-subtitle">Securely authenticating via Okta SSO</p>
          </div>
        )}
        <div className="login-footer">
          <p>&copy; 2026 Connect Agent Setup Portal</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;